// src/admin/pages/Users.jsx
import React, { useEffect, useMemo, useState } from "react";
import { adminService } from "../../services/adminService";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "../../UserContext";
import { Loader2, LogIn } from "lucide-react";
import { impersonationService } from "../../services/impersonationService";

const ROLE_IDS = {
  USER: "fe42ee60-9133-4f0e-b543-740d8697c23b",
  ADMIN: "ac9be322-db89-4beb-854a-46f2c91de374",
  SUPER_ADMIN: "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef",
};

const normalizeRole = (userRecord) => {
  if (userRecord.role_name) return userRecord.role_name;
  if (userRecord.user_role === ROLE_IDS.ADMIN) return "Admin";
  if (userRecord.user_role === ROLE_IDS.SUPER_ADMIN) return "Super Admin";
  if (userRecord.user_role === ROLE_IDS.USER) return "User";
  return userRecord.user_role || "--";
};

const Users = () => {
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [impersonationTarget, setImpersonationTarget] = useState(null);
  const [impersonationReason, setImpersonationReason] = useState("");
  const [impersonationTicketId, setImpersonationTicketId] = useState("");
  const [impersonationError, setImpersonationError] = useState("");
  const [isImpersonating, setIsImpersonating] = useState(false);


  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await adminService.getUsers();
        setUsers(data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [user]);

  const resolveUserId = (userRecord) =>
    userRecord.user_id || userRecord.userid || userRecord.id;

  const openImpersonationDialog = (userRecord) => {
    setImpersonationTarget(userRecord);
    setImpersonationReason("");
    setImpersonationTicketId("");
    setImpersonationError("");
  };

  const closeImpersonationDialog = () => {
    if (isImpersonating) return;
    setImpersonationTarget(null);
    setImpersonationReason("");
    setImpersonationTicketId("");
    setImpersonationError("");
  };

  const handleImpersonateUser = async (event) => {
    event.preventDefault();

    const targetUserId = impersonationTarget
      ? resolveUserId(impersonationTarget)
      : null;
    const reason = impersonationReason.trim();
    const ticketId = impersonationTicketId.trim();

    if (!targetUserId) {
      setImpersonationError("Missing user id.");
      return;
    }
    if (reason.length < 3) {
      setImpersonationError("Reason is required.");
      return;
    }

    setIsImpersonating(true);
    setImpersonationError("");

    try {
      const impersonatedUser = await impersonationService.impersonateUser(
        targetUserId,
        {
          reason,
          ticket_id: ticketId || undefined,
        }
      );
      impersonationService.storeImpersonatedSession(user, impersonatedUser);
      setUser(impersonatedUser);
      navigate("/");
    } catch (err) {
      console.error(err);
      setImpersonationError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to impersonate user."
      );
    } finally {
      setIsImpersonating(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let list = [...users];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((u) => {
        const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
        return (
          name.toLowerCase().includes(q) ||
          String(u.email || "").toLowerCase().includes(q) ||
          String(u.user_id || u.userid || u.id || "")
            .toLowerCase()
            .includes(q)
        );
      });
    }

    if (roleFilter !== "all") {
      list = list.filter((u) => normalizeRole(u) === roleFilter);
    }

    return list;
  }, [users, search, roleFilter]);

  const totals = useMemo(() => {
    const total = users.length;
    const admins = users.filter(
      (u) =>
        normalizeRole(u) === "Admin" || normalizeRole(u) === "Super Admin"
    ).length;
    const learners = users.filter((u) => normalizeRole(u) === "User").length;
    return { total, admins, learners };
  }, [users]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-[#54B0AF]" size={32} />
      </div>
    );
  }

  if (error) return <div className="text-red-600 text-title">{error}</div>;

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-display">Users</h1>

        <Link
          to="/admin/users/create"
          className="px-4 py-2 rounded-md bg-[#54B0AF] text-white text-sm hover:opacity-90 transition shadow"
        >
          + Create User
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Total users" value={totals.total} />
        <StatCard label="Admins" value={totals.admins} />
        <StatCard label="Learners" value={totals.learners} />
      </div>

      <div className="bg-white rounded-lg shadow stakeholder-summary-card p-4 flex flex-col md:flex-row md:items-center gap-4">
        <input
          type="text"
          placeholder="Search name, email, or ID"
          className="border rounded-md px-3 py-2 text-sm w-full md:w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="border rounded-md px-3 py-2 text-sm w-full md:w-56"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">All roles</option>
          <option value="User">User</option>
          <option value="Admin">Admin</option>
          <option value="Super Admin">Super Admin</option>
        </select>

        <div className="text-sm text-muted md:ml-auto">
          Showing {filteredUsers.length} of {users.length}
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-lg shadow stakeholder-summary-card p-6">
        <table className="w-full border-collapse text-[15px]">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="py-2 text-left text-title font-semibold">Name</th>
              <th className="py-2 text-left text-title font-semibold">Email</th>
              <th className="py-2 text-left text-title font-semibold">Role</th>
              <th className="py-2 text-left text-title font-semibold">Created</th>
              <th className="py-2 text-left text-title font-semibold">
                Progress
              </th>
              <th className="py-2 text-left text-title font-semibold">
                Support
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredUsers.map((u) => {
              const userId = resolveUserId(u);
              const roleLabel = normalizeRole(u);

              return (
                <tr
                  key={userId || u.email}
                  className="border-b hover:bg-gray-100 transition"
                >
                  <td className="py-2">
                    {u.first_name} {u.last_name}
                  </td>
                  <td>{u.email}</td>

                  <td>
                    <span className="px-2 py-1 bg-[#E6F3F3] text-[#246F6E] rounded text-xs font-medium">
                      {roleLabel}
                    </span>
                  </td>

                  <td className="text-muted">
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString()
                      : "--"}
                  </td>

                  <td>
                    {userId ? (
                      <Link
                        to={`/admin/users/${userId}/progress-summary`}
                        className="text-[#54B0AF] font-semibold hover:underline"
                      >
                        View summary
                      </Link>
                    ) : (
                      <span className="text-muted text-sm">--</span>
                    )}
                  </td>

                  <td>
                    {userId && userId !== user?.user_id ? (
                      <button
                        type="button"
                        onClick={() => openImpersonationDialog(u)}
                        className="inline-flex items-center gap-1 rounded-md border border-[#54B0AF] px-3 py-1.5 text-sm font-semibold text-[#246F6E] hover:bg-[#E6F3F3]"
                      >
                        <LogIn size={15} />
                        Impersonate
                      </button>
                    ) : (
                      <span className="text-muted text-sm">--</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center text-gray-500 py-6">
            No users found.
          </div>
        )}
      </div>

      {impersonationTarget && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4">
          <form
            onSubmit={handleImpersonateUser}
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
          >
            <h2 className="text-title">Impersonate user</h2>
            <p className="mt-2 text-sm text-muted">
              This will start an audited support session as{" "}
              {impersonationTarget.first_name} {impersonationTarget.last_name}.
            </p>

            <label className="mt-5 block text-sm font-semibold text-[#323232]">
              Reason
              <textarea
                value={impersonationReason}
                onChange={(e) => setImpersonationReason(e.target.value)}
                className="mt-2 h-24 w-full resize-none rounded-md border px-3 py-2 text-sm font-normal"
                placeholder="Why support/admin is impersonating"
                required
              />
            </label>

            <label className="mt-4 block text-sm font-semibold text-[#323232]">
              Ticket ID
              <input
                value={impersonationTicketId}
                onChange={(e) => setImpersonationTicketId(e.target.value)}
                className="mt-2 w-full rounded-md border px-3 py-2 text-sm font-normal"
                placeholder="Optional support ticket id"
              />
            </label>

            {impersonationError && (
              <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {impersonationError}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeImpersonationDialog}
                className="rounded-md border px-4 py-2 text-sm font-semibold text-[#323232]"
                disabled={isImpersonating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-md bg-[#54B0AF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                disabled={isImpersonating}
              >
                {isImpersonating ? "Starting..." : "Start impersonation"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Users;

const StatCard = ({ label, value }) => (
  <div className="bg-white shadow rounded-lg p-5 text-center stakeholder-summary-card">
    <p className="text-muted text-sm">{label}</p>
    <p className="text-2xl font-semibold text-[#323232] mt-1">{value}</p>
  </div>
);
